"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import Lenis from "lenis";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
import { registerScroller } from "@/lib/scroll-lock";

gsap.registerPlugin(ScrollTrigger, SplitText);

/**
 * The whole site's scroll motion runs from here.
 *
 * Pages stay Server Components and opt into animation declaratively:
 *   data-reveal="up" | "fade" | "scale"   — viewport reveal
 *   data-reveal-delay="0.15"              — optional lead-in
 *   data-split                            — heading revealed line by line
 *   data-parallax="-40"                   — px of travel across the viewport
 *
 * Reveals are driven by an IntersectionObserver rather than per-element
 * ScrollTriggers. Two reasons: it is one observer instead of ~70 triggers, and
 * it reports what is on screen regardless of how the reader got there — a
 * fling, an anchor jump or the End key cannot skip an element and strand it at
 * opacity 0, which is the classic failure of scroll-position-based reveals.
 */
export function MotionProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();

  // Smooth scrolling + the ScrollTrigger <-> Lenis handshake. Deliberately
  // outside the per-route effect so momentum survives navigation.
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const lenis = new Lenis({
      duration: 1.05,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      // Native scrolling on touch: hijacking it fights the browser's own
      // momentum and address-bar behaviour on mobile.
      syncTouch: false,
    });

    lenis.on("scroll", ScrollTrigger.update);
    registerScroller(lenis);

    const raf = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(raf);
    gsap.ticker.lagSmoothing(0);

    return () => {
      gsap.ticker.remove(raf);
      registerScroller(null);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    document.documentElement.setAttribute("data-motion-ready", "");

    if (reduced) {
      document.documentElement.classList.remove("js-motion");
      return;
    }

    const splits: SplitText[] = [];
    const observers: IntersectionObserver[] = [];
    let headingRevealed: WeakSet<HTMLElement> | null = null;

    const ctx = gsap.context(() => {
      // --- Blocks: viewport reveal -----------------------------------------
      const pending = new Set<HTMLElement>();
      let flushHandle = 0;

      // Elements crossing together animate together, so a row of cards reads
      // as one gesture rather than n unrelated ones.
      const flush = () => {
        flushHandle = 0;
        if (pending.size === 0) return;

        const batch = [...pending].sort(
          (a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top,
        );
        pending.clear();

        gsap.to(batch, {
          opacity: 1,
          y: 0,
          scale: 1,
          duration: 0.95,
          ease: "power3.out",
          overwrite: "auto",
          stagger: 0.08,
          delay: (_i, target: Element) =>
            Number((target as HTMLElement).dataset.revealDelay ?? 0),
        });
      };

      const revealObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const el = entry.target as HTMLElement;
            revealObserver.unobserve(el);
            pending.add(el);
          }
          if (pending.size && !flushHandle) {
            flushHandle = requestAnimationFrame(flush);
          }
        },
        // Start slightly before the element's edge clears the fold.
        { rootMargin: "0px 0px -10% 0px", threshold: 0 },
      );

      document
        .querySelectorAll<HTMLElement>("[data-reveal]")
        .forEach((el) => revealObserver.observe(el));
      observers.push(revealObserver);

      // --- Headings: masked line-by-line rise ------------------------------
      const headingObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const el = entry.target as HTMLElement;
            headingObserver.unobserve(el);
            headingRevealed?.add(el);

            const lines = el.querySelectorAll(".split-line");
            if (!lines.length) {
              gsap.set(el, { visibility: "visible" });
              continue;
            }

            gsap.to(lines, {
              yPercent: 0,
              duration: 1.05,
              ease: "power4.out",
              stagger: 0.09,
            });
          }
        },
        { rootMargin: "0px 0px -8% 0px", threshold: 0 },
      );
      observers.push(headingObserver);

      // Headings that have already played. `autoSplit` re-splits on resize and
      // on webfont load, and without this a heading the reader has already
      // seen would be re-parked below its mask and flash back in.
      const revealed = new WeakSet<HTMLElement>();

      document
        .querySelectorAll<HTMLElement>("[data-split]")
        .forEach((el) => {
          const split = SplitText.create(el, {
            type: "lines",
            mask: "lines",
            linesClass: "split-line",
            // Re-split on resize and once webfonts land, so the mask always
            // matches the real line boxes.
            autoSplit: true,
            // Keeps the original text exposed to assistive tech.
            aria: "auto",
            onSplit(self) {
              gsap.set(el, { visibility: "visible" });

              if (revealed.has(el)) {
                gsap.set(self.lines, { yPercent: 0 });
                return;
              }

              // Park the lines below their mask; the observer lifts them.
              gsap.set(self.lines, { yPercent: 115 });
              headingObserver.observe(el);
            },
          });

          splits.push(split);
        });

      headingRevealed = revealed;

      // --- Parallax ---------------------------------------------------------
      // Scrub genuinely needs scroll position, so this one stays on
      // ScrollTrigger.
      gsap.utils.toArray<HTMLElement>("[data-parallax]").forEach((el) => {
        gsap.to(el, {
          y: Number(el.dataset.parallax ?? -40),
          ease: "none",
          scrollTrigger: {
            trigger: el,
            start: "top bottom",
            end: "bottom top",
            scrub: true,
            invalidateOnRefresh: true,
          },
        });
      });
    });

    // Late-loading webfonts change every line box; refresh once they settle.
    document.fonts?.ready.then(() => ScrollTrigger.refresh());

    return () => {
      observers.forEach((observer) => observer.disconnect());
      splits.forEach((split) => split.revert());
      ctx.revert();
    };
  }, [pathname]);

  return <>{children}</>;
}
